import { UserOutlined, ShopOutlined, CheckOutlined, SendOutlined } from '@ant-design/icons';
import { App as AntApp, Avatar, Flex, Input, Button, theme } from 'antd';
import React, { useMemo, useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { updateOrderNote } from './OrderUtils';
import { useAuth } from '../../context/AuthContext';

export const MAX_NOTE_CHARS = 165;
/** Max hub/biz replies per order (aligned with customer app cust note cap). */
export const MAX_BIZ_NOTES_PER_ORDER = 5;

const formatTimestamp = (ts) => {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts;
    const datePart = d.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${datePart}, ${hh}:${mm}`;
  } catch {
    return ts;
  }
};

/** Double tick on hub/biz messages: customer has seen vs delivered only (admin view). */
function ReadReceipt({ read }) {
  const { token } = theme.useToken();
  const color = read ? token.colorPrimary : 'rgba(0,0,0,0.35)';
  return (
    <span
      aria-label={read ? 'Seen by customer' : 'Sent'}
      title={read ? 'Seen by customer' : 'Delivered'}
      style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, verticalAlign: 'middle' }}
    >
      <CheckOutlined style={{ fontSize: 11, color, marginRight: -5 }} />
      <CheckOutlined style={{ fontSize: 11, color }} />
    </span>
  );
}

function isShadowedByServer(local, serverList) {
  const tLocal = Date.parse(local.timestamp);
  if (Number.isNaN(tLocal)) return false;
  const by = (local.by || '').toLowerCase();
  return serverList.some((s) => {
    if ((s.by || '').toLowerCase() !== by) return false;
    if (s.notes !== local.notes) return false;
    if (!s.timestamp) return true;
    return Math.abs(Date.parse(s.timestamp) - tLocal) < 180000;
  });
}

const OrderNoteChat = ({ order, onRefreshOrders, messagesMaxHeight = 320, fillBody = false }) => {
  const { message } = AntApp.useApp();
  const { token } = useAuth();
  const listScrollRef = useRef(null);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localNotes, setLocalNotes] = useState([]);

  const list = useMemo(() => {
    const n = order?.rawData?.OrderNotes;
    return Array.isArray(n) ? n : [];
  }, [order?.rawData?.OrderNotes]);

  useEffect(() => {
    setLocalNotes([]);
    setDraft('');
  }, [order?.id]);

  useEffect(() => {
    setLocalNotes((prev) => prev.filter((ln) => !isShadowedByServer(ln, list)));
  }, [list]);

  const displayList = useMemo(() => [...list, ...localNotes], [list, localNotes]);

  const bizNoteCount = useMemo(
    () =>
      list.filter((n) => (n.by || '').toLowerCase() === 'biz').length + localNotes.length,
    [list, localNotes.length]
  );
  const canSendMore = bizNoteCount < MAX_BIZ_NOTES_PER_ORDER;

  useLayoutEffect(() => {
    const el = listScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [displayList, order?.id]);

  const hubId = order?.rawData?.HubId;
  const orderId = order?.rawData?.OrderId;
  const bizId = order?.rawData?.BizId ?? order?.bizId;

  const handleSend = useCallback(async () => {
    const text = draft.trim();
    if (!text) {
      message.warning('Enter a note before sending.');
      return;
    }
    if (text.length > MAX_NOTE_CHARS) {
      message.warning(`Notes are limited to ${MAX_NOTE_CHARS} characters.`);
      return;
    }
    if (!token || !hubId || !orderId || !bizId) {
      message.error('Missing order or session. Try again after refreshing.');
      return;
    }
    if (!canSendMore) {
      message.warning(`You can add at most ${MAX_BIZ_NOTES_PER_ORDER} hub notes per order.`);
      return;
    }
    setSubmitting(true);
    try {
      await updateOrderNote({
        token,
        HubId: hubId,
        BizId: bizId,
        OrderId: orderId,
        notes: text,
      });
      const ts = new Date().toISOString();
      setLocalNotes((prev) => [
        ...prev,
        {
          _localId: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          notes: text,
          by: 'Biz',
          timestamp: ts,
        },
      ]);
      setDraft('');
      message.success('Note sent.');
      if (typeof onRefreshOrders === 'function') {
        try {
          await onRefreshOrders();
        } catch {
          /* optimistic row remains */
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to send note.';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  }, [draft, token, hubId, orderId, bizId, onRefreshOrders, canSendMore, message]);

  return (
    <Flex vertical gap="middle" style={fillBody ? { height: '100%' } : undefined}>
      <div
        ref={listScrollRef}
        style={
          fillBody
            ? { flex: 1, minHeight: 0, overflowY: 'auto' }
            : { maxHeight: messagesMaxHeight, overflowY: 'auto' }
        }
      >
        <Flex vertical gap="small">
        {displayList.length === 0 ? (
          <div style={{ color: '#999', textAlign: 'center', padding: 24 }}>No notes yet.</div>
        ) : (
          displayList.map((item, index) => {
            const isBiz = (item.by || '').toLowerCase() === 'biz';
            const rowKey = item._localId ?? `srv-${index}-${item.timestamp ?? ''}-${(item.notes || '').slice(0, 12)}`;
            const bubbleBody = (
              <>
                <div>{item.notes}</div>
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 11,
                    color: 'rgba(0,0,0,0.45)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isBiz ? 'flex-end' : 'flex-start',
                  }}
                >
                  <span>{formatTimestamp(item.timestamp)}</span>
                  {isBiz && <ReadReceipt read={!!item.read} />}
                </div>
              </>
            );
            const bubbleStyle = {
              padding: '10px 12px',
              borderRadius: 12,
              maxWidth: '100%',
              wordBreak: 'break-word',
              backgroundColor: isBiz ? 'rgba(0,0,0,0.04)' : 'rgba(24, 144, 255, 0.08)',
              border: '1px solid rgba(0,0,0,0.06)',
            };
            return (
              <Flex
                key={rowKey}
                justify={isBiz ? 'flex-end' : 'flex-start'}
                style={{ width: '100%' }}
              >
                <div
                  style={{
                    maxWidth: '85%',
                    display: 'flex',
                    flexDirection: isBiz ? 'row-reverse' : 'row',
                    gap: 8,
                    alignItems: 'flex-start',
                  }}
                >
                  <Avatar
                    icon={isBiz ? <ShopOutlined /> : <UserOutlined />}
                    style={{
                      flexShrink: 0,
                      backgroundColor: isBiz ? 'var(--zh-brand-primary)' : 'var(--zh-success)',
                    }}
                  />
                  <div style={bubbleStyle}>{bubbleBody}</div>
                </div>
              </Flex>
            );
          })
        )}
        </Flex>
      </div>

      <div style={{ flexShrink: 0, padding: '12px 4px 4px' }}>
        <Flex gap={8} align="center">
          <Input.TextArea
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, MAX_NOTE_CHARS))}
            placeholder={
              canSendMore
                ? 'Reply to customer…'
                : `Hub note limit reached (${MAX_BIZ_NOTES_PER_ORDER} per order).`
            }
            maxLength={MAX_NOTE_CHARS}
            showCount
            disabled={!canSendMore || submitting}
            autoSize={{ minRows: 2, maxRows: 4 }}
            style={{
              flex: 1,
              border: '1px solid var(--zh-border-md)',
              borderStyle: 'solid',
              borderRadius: 10,
            }}
          />
          <Button
            type="primary"
            shape="circle"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={submitting}
            disabled={!canSendMore || !draft.trim()}
            aria-label="Send note"
            title="Send note"
          />
        </Flex>
      </div>
    </Flex>
  );
};

export default OrderNoteChat;
